import {assert, assertAndContinue} from 'Asserts';
import {
  PipelineStepDataKind,
  PipelineStepStatusCode,
  PipelineStepType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  PipelineStepRawData,
  PipelineViewGraphQL,
  pipelineStepRawDataIsKind,
  PipelineViewLineageNode,
} from 'pipelines/PipelineTypes';

interface SummarizedView {
  lineageLength: number;
  lastNode: {
    stepId: string;
    stepType: PipelineStepType;
    statusCode: PipelineStepStatusCode;
    hasStepOutputReference: boolean;
    rawDataIndex?: number | null;
    stepDataName?: string | undefined;
  } | null;
}

function summarizeLineageNode(lineageNode: PipelineViewLineageNode): SummarizedView['lastNode'] {
  return {
    stepId: lineageNode.step.id,
    stepType: lineageNode.step.stepType,
    statusCode: lineageNode.step.statusCode,
    hasStepOutputReference: lineageNode.stepOutputReference != null,
    rawDataIndex: lineageNode.stepOutputReference?.rawDataIndex,
    stepDataName: lineageNode.stepOutputReference?.outputStepData?.name,
  };
}

/**
 * Summarize view information for asserts.
 */
export function summarizeView(view: PipelineViewGraphQL): SummarizedView {
  const lastNode = view.lineage[view.lineage.length - 1];
  return {
    lineageLength: view.lineage.length,
    lastNode: lastNode ? summarizeLineageNode(lastNode) : null,
  };
}

interface HasViews {
  views: Array<PipelineViewGraphQL>;
}

/**
 * Return the child views for a given view.
 *
 * A child view is a view whose first node's step's incoming edge is from the last node of this view.
 */
export function getChildViewsForView(
  pipeline: HasViews,
  view: PipelineViewGraphQL
): Array<PipelineViewGraphQL> {
  if (view.lineage.length < 1) {
    return [];
  }

  const lastNode = view.lineage[view.lineage.length - 1];
  const step = lastNode.step;
  const stepOutputReference = lastNode.stepOutputReference;

  if (stepOutputReference == null) {
    return [];
  }

  return pipeline.views.filter((otherView) => {
    const firstStep = otherView.lineage[0].step;
    return firstStep.incomingEdges.some(
      (incomingEdge) =>
        incomingEdge.fromStep.id === step.id &&
        incomingEdge.fromStepData.id === stepOutputReference.outputStepData.id &&
        (incomingEdge.fromStepDataIndex === stepOutputReference.rawDataIndex ||
          // Mimics similar logic in get_views.py; if the edge has an index set but the steps aren't in a completed state, we still want to match
          (stepOutputReference.rawDataIndex == null && incomingEdge.fromStepDataIndex === 0))
    );
  });
}

/**
 * Return the parent views for a given view.
 *
 * A parent view is a view whose last node's step has an outgoing edge to any step in this view.
 */
export function getParentViewsForView(
  pipeline: HasViews,
  view: PipelineViewGraphQL
): Array<PipelineViewGraphQL> {
  const viewStepIds = new Set(view.lineage.map((lineageNode) => lineageNode.step.id));
  if (viewStepIds.size === 0) {
    return [];
  }

  return pipeline.views.filter((otherView) => {
    if (otherView.lineage.length === 0) {
      return false;
    }

    const lastStep = otherView.lineage[otherView.lineage.length - 1].step;
    return lastStep.outgoingEdges.some((outgoingEdge) => viewStepIds.has(outgoingEdge.toStep.id));
  });
}

/**
 * Return if all parent views have succeeded, i.e. their last lineage node is succeeded.
 */
export function allParentViewsSucceeded(pipeline: HasViews, view: PipelineViewGraphQL): boolean {
  return getParentViewsForView(pipeline, view).every(
    (parentView) =>
      parentView.lineage[parentView.lineage.length - 1].step.statusCode === PipelineStepStatusCode.SUCCEEDED
  );
}

/**
 * Get the output raw data for a view.
 *
 * Returns null if the view does not have an output data, which may occur for situations like:
 *  - The final step of the view hasn't been successfully run yet
 *  - The final step of the view doesn't produce outputs, e.g. the SEND_TO_DOCUMENT step
 *
 * When `dataKind` is provided, asserts that the output data is non-null and of the given kind.
 */
export function getOutputDataForView(view: PipelineViewGraphQL): PipelineStepRawData | null;
export function getOutputDataForView<K extends PipelineStepDataKind>(
  view: PipelineViewGraphQL,
  dataKind: K
): PipelineStepRawData<K>;
export function getOutputDataForView<K extends PipelineStepDataKind>(
  view: PipelineViewGraphQL,
  dataKind?: K
): PipelineStepRawData<K> | PipelineStepRawData | null {
  const lastNode = view.lineage[view.lineage.length - 1];
  if (lastNode == null) {
    if (dataKind != null) {
      assert(false, 'Expected non-empty view', {
        dataKind,
      });
    }
    return null;
  }
  if (dataKind != null) {
    return getOutputDataForLineageNode(lastNode, dataKind);
  } else {
    return getOutputDataForLineageNode(lastNode);
  }
}

export function getOutputDataForLineageNode(lineageNode: PipelineViewLineageNode): PipelineStepRawData | null;
export function getOutputDataForLineageNode<K extends PipelineStepDataKind>(
  lineageNode: PipelineViewLineageNode,
  dataKind: K
): PipelineStepRawData<K>;
export function getOutputDataForLineageNode<K extends PipelineStepDataKind>(
  lineageNode: PipelineViewLineageNode,
  dataKind?: K
): PipelineStepRawData<K> | PipelineStepRawData | null {
  if (lineageNode.stepOutputReference == null || lineageNode.stepOutputReference.rawDataIndex == null) {
    assert(dataKind == null, 'Expected lineage node to have output data', {
      lineageNode: summarizeLineageNode(lineageNode),
      dataKind,
    });
    return null;
  }
  const outputData =
    lineageNode.stepOutputReference.outputStepData.rawDatas[lineageNode.stepOutputReference.rawDataIndex];
  if (dataKind != null) {
    assert(
      pipelineStepRawDataIsKind(outputData, dataKind),
      'Expected lineageNode to have output data of specific kind',
      {lineageNode: summarizeLineageNode(lineageNode), dataKind}
    );
    return outputData as PipelineStepRawData<K>;
  }
  return outputData;
}

/**
 * Return true if the view outputs a data frame.
 */
export function isDataFrameView(view: PipelineViewGraphQL): boolean {
  const outputData = getOutputDataForView(view);
  return outputData != null && pipelineStepRawDataIsKind(outputData, PipelineStepDataKind.DATASET);
}

/**
 * Return true if the view outputs a chart.
 */
export function isChartView(view: PipelineViewGraphQL): boolean {
  const outputData = getOutputDataForView(view);
  return outputData != null && pipelineStepRawDataIsKind(outputData, PipelineStepDataKind.CHART);
}

/**
 * Return true if the view outputs a file.
 */
export function isFileView(view: PipelineViewGraphQL): boolean {
  const outputData = getOutputDataForView(view);
  return outputData != null && pipelineStepRawDataIsKind(outputData, PipelineStepDataKind.FILE);
}

/**
 * Return the client id for a given view.
 *
 * This function makes a best attempt at giving IDs to views. There are some major caveats.
 *
 * ### Issue with view ids
 *
 * Views fundamentally don't have an identity because they're data structures built on top of steps by the
 * backend. This means you can't access a view outside a given session, and they don't have stable IDs.
 *
 * ### View IDs
 *
 * Given the lack of identity, we still make a best attempt at composing an ID for views with this function.
 * We rely on the fact that the last node of a view can only be in 1 view ever. i.e., we never render the
 * same visual "view" twice in the UI.
 *
 * This means we can define the ID as the step id + the step output id (when present) + the raw data id (when present).
 *
 * ### Alternatives
 *
 * The best alternative would be to use the raw data index instead of the raw data id, which could be useful in some states,
 * but it seemed more reasonable to store state against a specific raw data. This is a trade-off that can
 * be revisited in the future.
 *
 * The other alternative is just storing the raw data id, but that's not always available.
 *
 * ### Caveats
 *
 * This ID has some weaknesses, but another way of thinking about it is that these cases are different views:
 * - It changes when the output of the last step in a view changes. This means it's good at storing state
 * against specific data but not a view.
 * - If we add a step at the end of a view, the id changes.
 *
 * If these caveats are unacceptable for your use case, you can use a different method generating the an ID
 * for a view.
 */
export function getDefaultClientIdForView(pipelineView: PipelineViewGraphQL): string {
  if (!assertAndContinue(pipelineView.lineage.length > 0, 'Lineage must have at least one node')) {
    return 'DEFAULT_CLIENT_ID';
  }

  const lastNode = pipelineView.lineage[pipelineView.lineage.length - 1];
  const stepOutputReference = lastNode.stepOutputReference;

  const idPart1 = lastNode.step.id;
  const idPart2 = stepOutputReference == null ? '' : `-${stepOutputReference.outputStepData.id}`;
  const rawDataIndex = stepOutputReference?.rawDataIndex;
  const idPart3 =
    stepOutputReference == null ||
    rawDataIndex == null ||
    !assertAndContinue(
      rawDataIndex < stepOutputReference.outputStepData.rawDatas.length,
      'rawDataIndex is out of bounds'
    )
      ? ''
      : `-${stepOutputReference.outputStepData.rawDatas[rawDataIndex].id}`;

  return `${idPart1}${idPart2}${idPart3}`;
}
